import { useMemo, useRef, useState } from 'react';
import * as PopoverPrimitive from '@radix-ui/react-popover';
import { Check, ChevronsUpDown, Search } from 'lucide-react';
import { Input } from './input';
import { cn } from '../lib/utils';

export interface ComboboxOption {
  value: string;
  label: string;
  /** Extra text to match against, e.g. an e-mail address. */
  keywords?: string;
}

interface ComboboxProps {
  options: ComboboxOption[];
  /** Selected value, if the caller keeps one. Omit for action-style pickers. */
  value?: string;
  onSelect: (value: string) => void;
  placeholder?: string;
  searchPlaceholder?: string;
  emptyMessage?: string;
  id?: string;
  className?: string;
  disabled?: boolean;
}

/**
 * Searchable single-select, replacing MUI's Autocomplete.
 *
 * Hand-rolled on Radix Popover rather than pulling in cmdk: the one use in this
 * app is picking an app user out of a list, and the filtering it needs is a
 * substring match over a label plus keywords.
 */
export const Combobox = ({
  options,
  value,
  onSelect,
  placeholder = 'Select…',
  searchPlaceholder = 'Search…',
  emptyMessage = 'No matches',
  id,
  className,
  disabled,
}: ComboboxProps) => {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const listId = useRef(`combobox-list-${Math.random().toString(36).slice(2)}`).current;

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return options;
    return options.filter(
      (option) =>
        option.label.toLowerCase().includes(q) ||
        (option.keywords ?? '').toLowerCase().includes(q)
    );
  }, [options, query]);

  const selectedLabel = options.find((option) => option.value === value)?.label;

  return (
    <PopoverPrimitive.Root
      open={open}
      onOpenChange={(next) => {
        setOpen(next);
        if (!next) setQuery('');
      }}
    >
      <PopoverPrimitive.Trigger asChild>
        <button
          id={id}
          type="button"
          role="combobox"
          aria-expanded={open}
          aria-controls={listId}
          disabled={disabled}
          className={cn(
            'flex h-10 w-full items-center justify-between gap-2 rounded-md border border-line-strong bg-surface px-3',
            'text-left font-sans text-base text-ink outline-none transition-colors',
            'focus-visible:border-brand focus-visible:ring-2 focus-visible:ring-brand/25',
            'disabled:bg-surface-sunk disabled:opacity-60',
            className
          )}
        >
          <span className={cn('truncate', !selectedLabel && 'text-ink-faint')}>
            {selectedLabel ?? placeholder}
          </span>
          <ChevronsUpDown aria-hidden className="size-4 shrink-0 text-ink-faint" />
        </button>
      </PopoverPrimitive.Trigger>

      <PopoverPrimitive.Portal>
        <PopoverPrimitive.Content
          align="start"
          sideOffset={4}
          className={cn(
            'z-50 w-[--radix-popover-trigger-width] overflow-hidden rounded-md border border-line bg-surface shadow-lg',
            'data-[state=open]:animate-overlay-in'
          )}
        >
          <div className="relative border-b border-line">
            <Search
              aria-hidden
              className="pointer-events-none absolute left-3 top-1/2 size-4 -translate-y-1/2 text-ink-faint"
            />
            <Input
              autoFocus
              value={query}
              onChange={(event) => setQuery(event.target.value)}
              placeholder={searchPlaceholder}
              aria-label={searchPlaceholder}
              className="border-0 pl-9 focus-visible:ring-0"
            />
          </div>

          <ul id={listId} role="listbox" className="max-h-64 overflow-y-auto p-1">
            {filtered.length === 0 ? (
              <li className="px-3 py-6 text-center font-sans text-sm text-ink-faint">
                {emptyMessage}
              </li>
            ) : (
              filtered.map((option) => {
                const isSelected = option.value === value;
                return (
                  <li key={option.value}>
                    <button
                      type="button"
                      role="option"
                      aria-selected={isSelected}
                      onClick={() => {
                        onSelect(option.value);
                        setOpen(false);
                        setQuery('');
                      }}
                      className={cn(
                        'flex w-full items-center gap-2 rounded-sm px-2 py-2 text-left',
                        'font-sans text-base text-ink outline-none',
                        'hover:bg-brand/10 focus-visible:bg-brand/10'
                      )}
                    >
                      <Check
                        aria-hidden
                        className={cn('size-4 shrink-0 text-brand', !isSelected && 'invisible')}
                      />
                      <span className="truncate">{option.label}</span>
                    </button>
                  </li>
                );
              })
            )}
          </ul>
        </PopoverPrimitive.Content>
      </PopoverPrimitive.Portal>
    </PopoverPrimitive.Root>
  );
};
